import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { Grid, GridItem } from '../../components/common/Layout';
import { reportService, userService } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { formatHoursMinutes, formatMinutes } from '../../utils/formatHours';

// Every figure on this page is an aggregate. The API publishes exact integer
// minutes next to the decimal hours; prefer the minutes, because `hours` is
// DECIMAL(5,2) and summing it drifts — three 7h20m days total 21.99h, which
// renders 21:59 while the rows above add up to 22:00. The decimal remains the
// fallback for a response cached before the minute fields shipped.
//   aggregateDuration(<INTEGER MINUTES>, <DECIMAL HOURS>)
const aggregateDuration = (minutes, hours) =>
  minutes === null || minutes === undefined || minutes === ''
    ? formatHoursMinutes(Number(hours) || 0)
    : formatMinutes(minutes);

// Styled components
const PageContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const StatsGrid = styled(Grid)`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.dark};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.medium};
`;

const StatChange = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-top: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme, negative }) => (negative ? theme.colors.error : theme.colors.success)};
`;

// Distribution bars
const DistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const DistRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const DistLabel = styled.div`
  min-width: 110px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DistBarTrack = styled.div`
  flex: 1;
  height: 10px;
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 9999px;
  overflow: hidden;
`;

const DistBarFill = styled.div`
  height: 100%;
  width: ${({ width }) => width}%;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 9999px;
`;

const DistValue = styled.div`
  min-width: 90px;
  text-align: right;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.medium};
  white-space: nowrap;
`;

// Filters
const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-width: 150px;
  background-color: ${({ theme }) => theme.colors.white};

  @media (max-width: 768px) {
    font-size: 16px; /* >=16px prevents iOS focus zoom */
    min-height: 44px;
  }
`;

const FilterInput = styled.input`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  @media (max-width: 768px) {
    font-size: 16px; /* >=16px prevents iOS focus zoom */
    min-height: 44px;
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 576px) {
    width: 100%;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};

    & > * {
      width: 100%;
    }
  }
`;

// Table
const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.light};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightAlt};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
`;

const TotalsRow = styled.tr`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  background-color: ${({ theme }) => theme.colors.light};
`;

// Status badge
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: 9999px;

  ${({ status, theme }) => {
    if (status === 'approved') {
      return `
        background-color: var(--status-approved-plate);
        color: ${theme.colors.success};
      `;
    } else if (status === 'pending') {
      return `
        background-color: var(--status-warning-plate);
        color: ${theme.colors.warning};
      `;
    } else if (status === 'challenged') {
      return `
        background-color: rgba(195,37,29,.25);
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const AlertBox = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: ${({ variant }) => (variant === 'error' ? '#F8D7DA' : '#D4EDDA')};
  color: ${({ theme, variant }) =>
    variant === 'error' ? theme.colors.error : theme.colors.success};
`;

// Format 'YYYY-MM' to e.g. 'Jul 2026'; leave other values untouched
const formatMonthLabel = (month, locale) => {
  if (typeof month === 'string' && /^\d{4}-\d{2}$/.test(month)) {
    const [year, monthNum] = month.split('-');
    return new Date(Number(year), Number(monthNum) - 1, 1)
      .toLocaleDateString(locale, { month: 'short', year: 'numeric' });
  }
  return month;
};

const pad2 = (n) => String(n).padStart(2, '0');

const toDateInputValue = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

// Distribution rows: [{label, hours, minutes?, percentage?}] rendered as bars.
// `hours` still drives the bar length (relative width, unaffected by rounding);
// `minutes` is what gets printed.
const DistributionBars = ({ rows }) => {
  const { t } = useTranslation();
  if (!rows || rows.length === 0) {
    return <div>{t('customer.reports.noData')}</div>;
  }

  const maxHours = Math.max(...rows.map((row) => Number(row.hours) || 0), 1);

  return (
    <DistList>
      {rows.map((row, index) => {
        const hours = Number(row.hours) || 0;
        const width = row.percentage != null
          ? Math.min(Number(row.percentage) || 0, 100)
          : (hours / maxHours) * 100;

        return (
          <DistRow key={index}>
            <DistLabel title={row.label}>{row.label}</DistLabel>
            <DistBarTrack>
              <DistBarFill width={width} />
            </DistBarTrack>
            {/* H:MM replaces the decimal + "h" suffix — "7:30h" would read wrong. */}
            <DistValue>
              {aggregateDuration(row.minutes, hours)}{row.percentage != null ? ` (${row.percentage}%)` : ''}
            </DistValue>
          </DistRow>
        );
      })}
    </DistList>
  );
};

const Reports = () => {
  const { t, i18n } = useTranslation();
  const statusLabel = (status) => t(`status.${status || 'pending'}`);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [employees, setEmployees] = useState([]);

  // Detailed report state
  const now = new Date();
  const [filters, setFilters] = useState({
    startDate: toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: toDateInputValue(now),
    employeeId: '',
    status: ''
  });
  const [detailed, setDetailed] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const report = await reportService.getCustomerReports();
        setSummary(report?.summary || null);
        setDistribution(report?.distribution || null);
      } catch (err) {
        setError(t('customer.reports.loadError'));
      } finally {
        setLoading(false);
      }
    };

    const fetchEmployees = async () => {
      try {
        const data = await userService.getCustomerEmployees({ limit: 1000, page: 1 });
        setEmployees(data?.items || []);
      } catch (err) {
        setEmployees([]);
      }
    };

    fetchData();
    fetchEmployees();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const buildReportParams = () => {
    const params = {
      startDate: filters.startDate,
      endDate: filters.endDate
    };
    if (filters.employeeId) params.employeeId = filters.employeeId;
    if (filters.status) params.status = filters.status;
    return params;
  };

  const handleGenerate = async () => {
    if (!filters.startDate || !filters.endDate) {
      setDetailError(t('customer.reports.dateRangeRequired'));
      return;
    }

    try {
      setGenerating(true);
      setDetailError(null);
      const data = await reportService.generateCustomerReport(buildReportParams());
      setDetailed(data);
    } catch (err) {
      setDetailError(t('customer.reports.generateError'));
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    if (!filters.startDate || !filters.endDate) {
      setDetailError(t('customer.reports.dateRangeRequired'));
      return;
    }

    try {
      setExporting(true);
      setDetailError(null);
      const blob = await reportService.exportCustomerReportToCsv(buildReportParams());
      reportService.downloadFile(blob, 'customer-report.csv');
    } catch (err) {
      setDetailError(t('customer.reports.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const changePercentage = Number(summary?.changePercentage) || 0;

  return (
    <PageContainer>
      {error && <AlertBox variant="error">{error}</AlertBox>}

      {loading ? (
        <div>{t('customer.reports.loading')}</div>
      ) : (
        <>
          {/* Summary tiles */}
          <StatsGrid columns={12} mobileColumns={12} gap={4}>
            <GridItem span={3} mobileSpan={6}>
              <Card padding="md" elevation="sm" fullHeight>
                <StatValue>{aggregateDuration(summary?.totalMinutes, summary?.totalHours)}</StatValue>
                <StatLabel>{t('customer.reports.totalHours')}</StatLabel>
              </Card>
            </GridItem>
            <GridItem span={3} mobileSpan={6}>
              <Card padding="md" elevation="sm" fullHeight>
                <StatValue>{aggregateDuration(summary?.approvedMinutes, summary?.approvedHours)}</StatValue>
                <StatLabel>{t('customer.reports.approvedHours')}</StatLabel>
              </Card>
            </GridItem>
            <GridItem span={3} mobileSpan={6}>
              <Card padding="md" elevation="sm" fullHeight>
                <StatValue>{aggregateDuration(summary?.pendingMinutes, summary?.pendingHours)}</StatValue>
                <StatLabel>{t('customer.reports.pendingHours')}</StatLabel>
              </Card>
            </GridItem>
            <GridItem span={3} mobileSpan={6}>
              <Card padding="md" elevation="sm" fullHeight>
                <StatValue>{aggregateDuration(summary?.challengedMinutes, summary?.challengedHours)}</StatValue>
                <StatLabel>{t('customer.reports.challengedHours')}</StatLabel>
              </Card>
            </GridItem>
            <GridItem span={4} mobileSpan={6}>
              <Card padding="md" elevation="sm" fullHeight>
                <StatValue>{aggregateDuration(summary?.currentMonthMinutes, summary?.currentMonthHours)}</StatValue>
                <StatLabel>{t('customer.reports.hoursThisMonth')}</StatLabel>
                <StatChange negative={changePercentage < 0}>
                  {t('customer.reports.vsPreviousMonth', { change: `${changePercentage >= 0 ? '+' : ''}${changePercentage}` })}
                </StatChange>
              </Card>
            </GridItem>
            <GridItem span={4} mobileSpan={6}>
              <Card padding="md" elevation="sm" fullHeight>
                <StatValue>{Number(summary?.employeeCount) || 0}</StatValue>
                <StatLabel>{t('customer.reports.employees')}</StatLabel>
              </Card>
            </GridItem>
            <GridItem span={4} mobileSpan={12}>
              <Card padding="md" elevation="sm" fullHeight>
                <StatValue>{Number(summary?.totalEntries) || 0}</StatValue>
                <StatLabel>{t('customer.reports.totalEntries')}</StatLabel>
              </Card>
            </GridItem>
          </StatsGrid>

          {/* Distributions */}
          <Grid columns={12} mobileColumns={12} gap={6} style={{ marginBottom: '1.5rem' }}>
            <GridItem span={6} mobileSpan={12}>
              <Card title={t('customer.reports.hoursByMonth')} padding="md" fullHeight>
                <DistributionBars
                  rows={(distribution?.byMonth || []).map((row) => ({
                    label: formatMonthLabel(row.month, i18n.language),
                    hours: row.hours,
                    minutes: row.minutes
                  }))}
                />
              </Card>
            </GridItem>
            <GridItem span={6} mobileSpan={12}>
              <Card title={t('customer.reports.hoursByEmployee')} padding="md" fullHeight>
                <DistributionBars
                  rows={(distribution?.byEmployee || []).map((row) => ({
                    label: row.name,
                    hours: row.hours,
                    minutes: row.minutes,
                    percentage: row.percentage
                  }))}
                />
              </Card>
            </GridItem>
            <GridItem span={6} mobileSpan={12}>
              <Card title={t('customer.reports.hoursByStatus')} padding="md" fullHeight>
                <DistributionBars
                  rows={(distribution?.byStatus || []).map((row) => ({
                    label: statusLabel(row.status),
                    hours: row.hours,
                    minutes: row.minutes,
                    percentage: row.percentage
                  }))}
                />
              </Card>
            </GridItem>
            <GridItem span={6} mobileSpan={12}>
              <Card title={t('customer.reports.hoursByProject')} padding="md" fullHeight>
                <DistributionBars
                  rows={(distribution?.byProject || []).map((row) => ({
                    label: row.name,
                    hours: row.hours,
                    minutes: row.minutes,
                    percentage: row.percentage
                  }))}
                />
              </Card>
            </GridItem>
          </Grid>
        </>
      )}

      {/* Detailed report */}
      <Card
        title={t('customer.reports.detailedTitle')}
        subtitle={t('customer.reports.detailedSubtitle')}
        padding="md"
      >
        {detailError && <AlertBox variant="error">{detailError}</AlertBox>}

        <FilterContainer>
          <FilterGroup>
            <FilterLabel htmlFor="startDate">{t('customer.reports.from')}</FilterLabel>
            <FilterInput
              id="startDate"
              name="startDate"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="endDate">{t('customer.reports.to')}</FilterLabel>
            <FilterInput
              id="endDate"
              name="endDate"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="employeeId">{t('common.employee')}</FilterLabel>
            <FilterSelect
              id="employeeId"
              name="employeeId"
              value={filters.employeeId}
              onChange={handleFilterChange}
            >
              <option value="">{t('customer.reports.allEmployees')}</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name || `${employee.firstName} ${employee.lastName}`}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="status">{t('common.status')}</FilterLabel>
            <FilterSelect
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">{t('customer.reports.allStatuses')}</option>
              <option value="pending">{t('status.pending')}</option>
              <option value="approved">{t('status.approved')}</option>
              <option value="challenged">{t('status.challenged')}</option>
            </FilterSelect>
          </FilterGroup>

          <FilterActions>
            <Button onClick={handleGenerate} disabled={generating}>
              {generating ? t('customer.reports.generating') : t('customer.reports.generate')}
            </Button>
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              {exporting ? t('customer.reports.exporting') : t('customer.reports.exportCsv')}
            </Button>
          </FilterActions>
        </FilterContainer>

        {detailed && (
          <TableContainer>
            <Table>
              <TableHead>
                <tr>
                  <TableHeadCell>{t('common.date')}</TableHeadCell>
                  <TableHeadCell>{t('common.employee')}</TableHeadCell>
                  <TableHeadCell>{t('common.project')}</TableHeadCell>
                  <TableHeadCell>{t('common.hours')}</TableHeadCell>
                  <TableHeadCell>{t('common.status')}</TableHeadCell>
                  <TableHeadCell>{t('common.description')}</TableHeadCell>
                </tr>
              </TableHead>
              <tbody>
                {(detailed.entries || []).length > 0 ? (
                  <>
                    {detailed.entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{entry.date}</TableCell>
                        <TableCell>{entry.employeeName}</TableCell>
                        <TableCell>{entry.projectName || '-'}</TableCell>
                        {/* generateReport returns plain rows (no durationFormatted):
                            exact minutes when present, decimal hours otherwise.
                            Not aggregateDuration — a row with no value keeps
                            showing a dash rather than a made-up 0:00. */}
                        <TableCell>
                          {entry.durationMinutes == null
                            ? formatHoursMinutes(entry.hours)
                            : formatMinutes(entry.durationMinutes)}
                        </TableCell>
                        <TableCell>
                          <Badge status={entry.status || 'pending'}>
                            {statusLabel(entry.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{entry.description || '-'}</TableCell>
                      </TableRow>
                    ))}
                    <TotalsRow>
                      <TableCell colSpan={3}>
                        {t('customer.reports.totalEntriesLabel', { count: detailed.total?.entries ?? detailed.entries.length })}
                      </TableCell>
                      <TableCell>{aggregateDuration(detailed.total?.minutes, detailed.total?.hours)}</TableCell>
                      <TableCell colSpan={2} />
                    </TotalsRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                      {t('customer.reports.noEntriesFound')}
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </PageContainer>
  );
};

export default Reports;
